import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import { Card } from '../components/common/Card'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import { TaskApprovalScreen } from '../components/features/TaskApprovalScreen'
import { useAuth } from '../context/AuthContext'
import { isAssignableReviewRole } from '../lib/config/role-config'
import { TASK_STATUS_COLORS, TASK_STATUS_LABELS } from '../lib/config/project-config'
import { canProfileSeeStatus } from '../lib/config/status-config'
import { mutations } from '../lib/supabase/mutations'
import { queries } from '../lib/supabase/queries'
import { sendTaskAssignedEmail, sendTaskReviewOutcomeEmail } from '../utils/emailNotifications'
import { isTaskOverallDueOverdue, isTaskStatusOverdue } from '../utils/statusDates'

const normalizeResponsiblePerson = (value: string | null | undefined) => value?.trim().toLowerCase() || ''

const CHART_COLORS = ['#0f766e', '#dc2626', '#2563eb', '#d97706', '#7c3aed', '#059669', '#be185d']

type ChartDatum = {
  label: string
  value: number
  color: string
}

const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0]
    return (
      <div className="rounded bg-white p-2 shadow-lg">
        <p className="text-xs font-semibold text-gray-900">{data.name}: {data.value}</p>
      </div>
    )
  }
  return null
}

const ChartCard: React.FC<{
  title: string
  data: ChartDatum[]
  emptyMessage: string
  legendData?: ChartDatum[]
}> = ({ title, data, emptyMessage, legendData }) => (
  <Card
    header={<h2 className="font-semibold text-gray-900">{title}</h2>}
    className="flex flex-col"
  >
    {data.length === 0 ? (
      <div className="flex flex-1 items-center justify-center text-sm text-gray-500">{emptyMessage}</div>
    ) : (
      <div className="flex flex-col gap-3">
        <div className="h-[128px] md:h-[140px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="label"
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={65}
                paddingAngle={2}
              >
                {data.map((entry, index) => (
                  <Cell key={`${entry.label}-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-2">
          {(legendData || data).map((item, index) => (
            <div key={`${item.label}-${index}`} className="flex items-center gap-2 text-xs">
              <div className="h-2 w-2 shrink-0 rounded" style={{ backgroundColor: item.color }} />
              <span className="truncate text-gray-700 flex-1">{item.label}</span>
              <span className="font-medium text-gray-900">{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    )}
  </Card>
)

export const DashboardPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { profile } = useAuth()
  const [reviewActionTaskId, setReviewActionTaskId] = useState<string | null>(null)

  const statusQuery = useQuery({
    queryKey: ['dashboard', 'status'],
    queryFn: queries.getTasksByStatus,
  })

  const assigneeQuery = useQuery({
    queryKey: ['dashboard', 'assignee'],
    queryFn: queries.getTasksByAssignee,
  })

  const appQuery = useQuery({
    queryKey: ['dashboard', 'app'],
    queryFn: queries.getTasksByApp,
  })

  const tasksQuery = useQuery({
    queryKey: ['tasks', 'dashboard-review'],
    queryFn: () => queries.getTasks(false),
  })

  const profilesQuery = useQuery({
    queryKey: ['profiles', 'all'],
    queryFn: queries.getAllProfiles,
  })

  const relatedAppsQuery = useQuery({
    queryKey: ['related-apps'],
    queryFn: queries.getRelatedApps,
  })

  const updateTaskMutation = useMutation({
    mutationFn: ({ taskId, updates }: { taskId: string; updates: Parameters<typeof mutations.updateTask>[1] }) =>
      mutations.updateTask(taskId, updates),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tasks'] }),
        queryClient.invalidateQueries({ queryKey: ['dashboard'] }),
        queryClient.invalidateQueries({ queryKey: ['tasks', 'kanban'] }),
      ])
    },
  })

  const isLoading = statusQuery.isLoading || assigneeQuery.isLoading || appQuery.isLoading || tasksQuery.isLoading || profilesQuery.isLoading || relatedAppsQuery.isLoading
  const error = statusQuery.error || assigneeQuery.error || appQuery.error || tasksQuery.error || profilesQuery.error || relatedAppsQuery.error

  const profileMap = useMemo(
    () => new Map((profilesQuery.data || []).map((item) => [item.id, item.full_name || item.email || 'Unknown user'])),
    [profilesQuery.data]
  )
  const reviewAssigneeOptions = useMemo(
    () =>
      (profilesQuery.data || [])
        .filter((item) => isAssignableReviewRole(item.role))
        .map((item) => ({ id: item.id, label: item.full_name || item.email || 'Unknown user' })),
    [profilesQuery.data]
  )
  const relatedAppResponsiblePersonMap = useMemo(() => {
    const profiles = profilesQuery.data || []
    const relatedApps = relatedAppsQuery.data || []

    const normalizedProfiles = profiles.map((item) => ({
      id: item.id,
      fullName: normalizeResponsiblePerson(item.full_name),
      email: normalizeResponsiblePerson(item.email),
    }))

    return new Map(
      relatedApps.map((app) => {
        const responsiblePerson = normalizeResponsiblePerson(app.responsible_person)
        if (!responsiblePerson) {
          return [app.id, null]
        }

        const matchedProfile = normalizedProfiles.find(
          (item) => item.id === responsiblePerson || item.fullName === responsiblePerson || item.email === responsiblePerson
        )
        return [app.id, matchedProfile?.id || null]
      })
    )
  }, [profilesQuery.data, relatedAppsQuery.data])

  const newRequestTaskList = useMemo(
    () => (tasksQuery.data || []).filter((task) => task.status === 'new'),
    [tasksQuery.data]
  )
  const unassignedTaskCount = useMemo(
    () => (tasksQuery.data || []).filter((task) => canProfileSeeStatus(profile, task.status) && !task.assigned_to).length,
    [tasksQuery.data, profile]
  )
  const overdueTaskCount = useMemo(
    () =>
      (tasksQuery.data || []).filter((task) => {
        if (!canProfileSeeStatus(profile, task.status) || task.status === 'completed') {
          return false
        }

        return isTaskStatusOverdue(task) || isTaskOverallDueOverdue(task)
      }).length,
    [tasksQuery.data, profile]
  )


  // Helper to group legend items
  function getLegendData(data: ChartDatum[]): ChartDatum[] {
    if (data.length <= 3) return data
    const sorted = [...data].sort((a, b) => b.value - a.value)
    const top3 = sorted.slice(0, 3)
    const otherValue = sorted.slice(3).reduce((sum, item) => sum + item.value, 0)
    if (otherValue === 0) return top3
    return [
      ...top3,
      { label: 'Other', value: otherValue, color: '#d1d5db' },
    ]
  }

  // Status
  const statusRaw: ChartDatum[] = (statusQuery.data || [])
    .filter((item) => canProfileSeeStatus(profile, item.status))
    .map((item) => ({
      label: TASK_STATUS_LABELS[item.status as keyof typeof TASK_STATUS_LABELS] || item.status,
      value: item.count,
      color: TASK_STATUS_COLORS[item.status as keyof typeof TASK_STATUS_COLORS] || '#6b7280',
    }))
  const statusLegend = getLegendData(statusRaw)

  // Assignee
  const assigneeRaw: ChartDatum[] = (assigneeQuery.data || []).map((item, index) => ({
    label: item.full_name || 'Unassigned',
    value: item.count,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))
  const assigneeLegend = getLegendData(assigneeRaw)

  // App
  const appRaw: ChartDatum[] = (appQuery.data || []).map((item, index) => ({
    label: item.related_app,
    value: item.count,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))
  const appLegend = getLegendData(appRaw)


  // Age bucket chart data
  const ageQuery = useQuery({
    queryKey: ['dashboard', 'age-buckets'],
    queryFn: queries.getTasksByAgeBucket,
  })
  const ageData: ChartDatum[] = (ageQuery.data || []).map((item, index) => ({
    label: item.bucket,
    value: item.count,
    color: CHART_COLORS[index % CHART_COLORS.length],
  }))

  const activeTaskCount = statusRaw.reduce((total, item) => total + item.value, 0)

  const handleReviewStatusChange = async (taskId: string, status: 'approved' | 'rejected', assigneeId: string | null = null) => {
    if (status === 'approved' && !assigneeId) {
      return
    }

    setReviewActionTaskId(taskId)

    try {
      const tasks = tasksQuery.data || []
      const profiles = profilesQuery.data || []
      const targetTask = tasks.find((task) => task.id === taskId)
      const updates: Parameters<typeof mutations.updateTask>[1] = { status }
      if (status === 'approved') {
        updates.assigned_to = assigneeId
      }

      await updateTaskMutation.mutateAsync({
        taskId,
        updates,
      })

      if (targetTask) {
        const updatedTask = {
          ...targetTask,
          status,
          assigned_to: status === 'approved' ? assigneeId : targetTask.assigned_to,
        }

        const notifications: Array<Promise<boolean>> = []
        const creatorProfile = profiles.find((item) => item.id === targetTask.created_by)
        const creatorEmail = creatorProfile?.email
        const reviewerName = profile?.full_name || profile?.email || 'Task approver'
        if (creatorEmail) {
          notifications.push(sendTaskReviewOutcomeEmail(creatorEmail, updatedTask, status, reviewerName))
        }

        if (status === 'approved' && assigneeId) {
          const assignedProfile = profiles.find((item) => item.id === assigneeId)
          if (assignedProfile?.email) {
            notifications.push(sendTaskAssignedEmail(assignedProfile.email, updatedTask))
          }
        }

        if (notifications.length > 0) {
          await Promise.allSettled(notifications)
        }
      }
    } finally {
      setReviewActionTaskId(null)
    }
  }

  return (
    <div className="space-y-3">
      <TaskApprovalScreen
        isTaskApprover={Boolean(profile?.is_task_approver)}
        tasks={newRequestTaskList}
        relatedAppResponsiblePersonMap={relatedAppResponsiblePersonMap}
        profileMap={profileMap}
        assigneeOptions={reviewAssigneeOptions}
        activeTaskId={reviewActionTaskId}
        onApprove={(task, assigneeId) => handleReviewStatusChange(task.id, 'approved', assigneeId)}
        onReject={(task) => handleReviewStatusChange(task.id, 'rejected')}
      />

      <div className="flex flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-xs text-gray-600">Current workload across statuses, owners, and related apps.</p>
        </div>
        <div className="rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-900">
          {activeTaskCount} active task{activeTaskCount === 1 ? '' : 's'} tracked
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-left transition hover:bg-amber-100"
          onClick={() => navigate('/tasks', { state: { dashboardTaskFilter: 'unassigned' } })}
        >
          <p className="text-xs uppercase tracking-wide text-amber-700">Unassigned Tasks</p>
          <p className="mt-1 text-2xl font-bold text-amber-900">{unassignedTaskCount}</p>
          <p className="text-xs text-amber-700">Open filtered list</p>
        </button>
        <button
          type="button"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-left transition hover:bg-red-100"
          onClick={() => navigate('/tasks', { state: { dashboardTaskFilter: 'overdue' } })}
        >
          <p className="text-xs uppercase tracking-wide text-red-700">Overdue Tasks</p>
          <p className="mt-1 text-2xl font-bold text-red-900">{overdueTaskCount}</p>
          <p className="text-xs text-red-700">Open filtered list</p>
        </button>
      </div>

      {isLoading && (
        <Card>
          <div className="flex min-h-[200px] items-center justify-center">
            <LoadingSpinner text="Loading dashboard data..." />
          </div>
        </Card>
      )}

      {error && !isLoading && (
        <Card>
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error instanceof Error ? error.message : 'Failed to load dashboard data.'}
          </div>
        </Card>
      )}


      {!isLoading && !error && (
        <div className="grid gap-3 md:grid-cols-2">
          <ChartCard
            title="Tasks by Status"
            data={statusRaw}
            legendData={statusLegend}
            emptyMessage="No active tasks available for status reporting."
          />
          <ChartCard
            title="Tasks by Assignee"
            data={assigneeRaw}
            legendData={assigneeLegend}
            emptyMessage="No assigned tasks available yet."
          />
          <ChartCard
            title="Tasks by App"
            data={appRaw}
            legendData={appLegend}
            emptyMessage="No related app data available yet."
          />
          <ChartCard
            title="Average Age of Tasks"
            data={ageData}
            emptyMessage="No tasks available for age analysis."
          />
        </div>
      )}
    </div>
  )
}
